// src/views/MenageTodayView.jsx — « MON PLANNING » DE L'AGENT DE MÉNAGE, d'après
// la maquette NEST v677 (espace Ménage, `carteRun` et la journée restructurée
// du 28/10), à 1280 et 390 px.
//
// CE QUE L'ÉCRAN MONTRE, dans l'ordre de la maquette :
//   1. la date, « Bonjour <prénom> », quatre tuiles qui ouvrent leur liste
//      (ménages · contrôle · à reprendre · changements) ;
//   2. la bascule Aujourd'hui / Le mois ;
//   3. PROCHAIN (une carte en grand), ENSUITE · N, FAIT · N (repliées).
//
// D'OÙ VIENNENT LES DONNÉES : la porte `ecran_agent_planning_lire` (ses ménages,
// ses changements, ses logements, son prénom), la porte `menage_log_info`
// (adresse, code, à apporter), `controle_a_controler`, `controle_agent_reprises`.
// Aucune lecture directe de `menage_runs`, `menage_evenements_agent`,
// `menage_day_closures` ni de la configuration. Une lecture ratée se dit : une
// tuile affiche « — », jamais 0.
//
// La maquette ne montre ni « Journée clôturée » ni la carte des avis (qui vivent
// dans Mon travail) : ils ne sont plus ici. L'envoi du récapitulatif de fin de
// journée, lui, reste : l'admin le reçoit toujours.

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { supabase } from "../supabase"
import { CalendarDays, CalendarRange, CircleCheck, X, RefreshCw } from "lucide-react"
import { firstNameFromEmail, libelleDepuisEmail } from "./menage/RunCard"
// CHANTIER 42 Phase 4 (ADR-302) — consignes internes : lecture RLS-directe,
// fail-closed (ne throw jamais).
import { fetchMyInstructions } from "../lib/menage-instructions-api"
import CarteMenageAgent from "./menage/CarteMenageAgent"
import CarteInterventionAgent from "./menage/CarteInterventionAgent"
import ReprisesAgent from "./menage/ReprisesAgent"
import MonMoisAgent from "./menage/MonMoisAgent"
import CarteControleAgent, { heureLimiteControle } from "./menage/CarteControleAgent"
import BandeauAgent from "./menage/BandeauAgent"
import { CartePlanning, BoutonCarte, BoutonIncident } from "./menage/CartePlanning"
import { NEST, POLICE } from "./menage/palette-nest"
import { fetchAControler } from "../lib/controle-api"
import { fusionnerJournee, estFaite, heureInterventionMin } from '../lib/menage-journee-interventions'
import { chargerInterventions, validerIntervention } from "../lib/menage-frise-api"
import { retraitsAMontrer, ajoutsAMontrer, heuresAMontrer, datesAMontrer } from "../lib/menage-evenements-bandeau"
import { fetchLogInfo } from "../lib/menage-log-info-cache"
import { dureeDuRunAgent, rangDuMenage } from "../lib/menage-journee-agent"
import { fetchJourneeAgent } from "../lib/agent-journee-api"
import {
  dateTitreFR, heureFinParis, ligneChangement, rangerJournee, titreProchain, tuilesBandeau,
} from "../lib/agent-journee-regles"
import { getEffectiveRunStatus } from "../lib/menage-status-helpers"
import { useMenagePhotoUploadQueue } from "../lib/use-photo-upload-queue"
import { commitUploadedPhoto } from "../lib/menage-photo-commit"
import { STATUS, createIncident } from "../lib/incidents-api"
import IncidentModal from "../modals/IncidentModal"
import ChecklistRunModal from "../modals/ChecklistRunModal"
// CHANTIER 42 Phase 3 (extraction) — RunCard + sous-composants déplacés dans
// ./menage/RunCard (move byte-identique). RE-EXPORT COMPAT EXHAUSTIF ci-dessous :
// AccessSection / TimingSection / GuestCountLine / GuestRealTimesLine restent
// importables depuis MenageTodayView (consommés par ChecklistRunModal,
// GuestTimesLine.test, TimingSection.test, etc.).
import { ordonnerJournee, heureEffective, formaterDuree, formaterHeureCourte } from "../lib/menage-frise"
import {
  AccessSection,
  TimingSection,
  GuestCountLine,
  GuestRealTimesLine,
} from "./menage/RunCard"

export { AccessSection, TimingSection, GuestCountLine, GuestRealTimesLine }

export function todayParisIso() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date())
}

// PR #151 AMENDEMENT (P3) — heure courante Paris au format "HH:MM"
// pour le verrouillage check-out time. Utilisé par RunCard et
// ChecklistRunModal via getEffectiveRunStatus(run, today, nowHHMM, ...).
export function nowParisHHMM() {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date())
}

// CHANTIER 42 Phase 3 Lot 1 (amendement B) — pool d'AFFICHAGE des runs.
// Pour l'orchestrateur : self + son pool d'agents managés (dédupliqué — D :
// si self ∈ managed_agents, jamais en double). Pour tout autre rôle : [self].
// ⚠️ N'EST UTILISÉ QUE POUR L'AFFICHAGE (.in()). La clôture/récap reste SELF
// (.eq currentUser.email) — voir handleRunCompleted. Le cloisonnement réel
// (pool ∧ logIds) est garanti côté serveur par la RLS orchestrateur_select_pool.
/**
 * CE RÔLE VOIT-IL TOUT LE PARC, SANS PÉRIMÈTRE ?
 *
 * 🔴 POURQUOI CE HELPER EXISTE (défaut mesuré à l'écran le 2026-08-12). Une
 * liste vide de `computeVisibleEmails` porte DEUX sens opposés :
 *   · pour un agent ou un orchestrateur → « aucun périmètre, donc RIEN » —
 *     fail-closed, et il doit le rester ;
 *   · pour l'admin → « aucun périmètre PARCE QU'IL VOIT TOUT ».
 * Les faire porter par la même valeur vide a produit un écran de pilotage à
 * « 0 ménage » : le garde-fou fail-closed coupait la requête AVANT qu'elle ne
 * parte, alors même que le filtre client était bien levé en aval. Le défaut
 * n'était pas dans le filtre, il était dans le sens donné au vide.
 *
 * Tout garde-fou qui coupe sur `!visibleEmails.length` doit donc consulter
 * celui-ci — et surtout PAS être remplacé par un `role === 'admin'` recopié sur
 * place, qui redeviendrait invisible au prochain lecteur.
 */
export function voitToutLeParc(currentUser) {
  return currentUser?.role === 'admin'
}

export function computeVisibleEmails(currentUser) {
  const self = currentUser?.email
  // Pas d'identité (email absent) → rien (défensif : un user sans email ne voit
  // aucun run, même un orchestrateur avec un pool).
  if (!self) return []
  // L'admin n'a pas de « liste visible » : il voit tout (cf.
  // buildRunsProviderFilter). On rend [] plutôt que [lui] — une liste d'un seul
  // nom se lisait comme un périmètre, alors qu'il n'y en a pas.
  if (currentUser?.role === 'admin') return []
  if (currentUser?.role !== 'orchestrateur') return [self]
  const pool = Array.isArray(currentUser.managed_agents) ? currentUser.managed_agents : []
  return [...new Set([self, ...pool].filter(Boolean))]
}

// CHANTIER 42 Phase 3 Lot 2 — options du sélecteur « Assigner à » (orchestrateur).
// Construit la liste { value, label, isSelf } des cibles de réassignation :
// soi-même (« Moi ») + chaque agent du pool managé, dédupliqué (D : si self ∈
// managed_agents, jamais en double — self prime, libellé « Moi »). Retourne []
// pour tout rôle ≠ orchestrateur (le sélecteur n'est jamais rendu hors rôle).
// Helper PUR (testable) : aucune autorisation conférée ici — le cloisonnement
// réel est la RLS orchestrateur_assign + le trigger enforce_*. Ce champ ne sert
// qu'à peupler le <select> côté UI.
export function computeAssignOptions(currentUser, { agentsConnus = [], prenoms = {} } = {}) {
  // LOT PRÉNOMS (2026-08-19) : le prénom SAISI (Paramètres → Comptes, servi
  // par pool_prenoms) prime ; la dérivation d'email reste EN FILET — jamais
  // l'email brut à l'écran.
  const libelle = (email) => prenoms[String(email || '').toLowerCase()] || libelleDepuisEmail(email)
  // ⚠️ POUR L'ADMIN, CE N'EST PAS « MON POOL » MAIS TOUS LES AGENTS. Élargir le
  // pool aurait été un contresens : il n'en a pas. On lui compose la liste
  // depuis les agents RÉELLEMENT rencontrés dans la fenêtre chargée — donnée
  // vraie, aucun endpoint nouveau, et rien à maintenir en double.
  if (currentUser?.role === 'admin') {
    const self = currentUser?.email
    const vus = [...new Set((agentsConnus || []).filter(Boolean))].sort()
    const options = self ? [{ value: self, label: 'Moi', isSelf: true }] : []
    for (const email of vus) {
      if (email === self) continue
      options.push({ value: email, label: libelle(email), isSelf: false })
    }
    return options
  }
  if (currentUser?.role !== 'orchestrateur') return []
  const self = currentUser?.email
  if (!self) return []
  const pool = Array.isArray(currentUser.managed_agents) ? currentUser.managed_agents : []
  const seen = new Set([self])
  const options = [{ value: self, label: 'Moi', isSelf: true }]
  for (const email of pool) {
    if (!email || seen.has(email)) continue
    seen.add(email)
    // C-1 : le pool orchestrateur ne porte QUE des emails (claim
    // managed_agents) — le prénom se DÉRIVE, l'email ne s'affiche jamais.
    options.push({ value: email, label: libelle(email), isSelf: false })
  }
  return options
}

// MÉNAGES ORPHELINS (migration 20261119000001, B2 revue adversariale) — filtre
// prestataire PARTAGÉ des requêtes d'AFFICHAGE menage_runs. La migration seule
// ne montre RIEN : un `.in(..., visibleEmails)` sur l'agent assigné ne matche
// JAMAIS une valeur NULL (sémantique SQL IN). Ce helper applique donc :
//   · rôle menage (et tout autre)      → `.in(...)` INCHANGÉ ;
//   · rôle orchestrateur               → `.or(in.(...) , is.null)` : le pool
//     d'affichage + les runs SANS agent (la RLS orchestrateur_select_non_assigne
//     borne côté serveur : logIds ∧ status ∧ 48 h).
// SYNTAXE PostgREST : dans `in.(...)` d'un `.or()`, les emails contiennent `@`
// et des points → ils DOIVENT être entre guillemets doubles. Un email issu
// d'auth.users ne peut pas contenir de guillemet ni d'antislash (Supabase Auth
// les refuse) ; par défense en profondeur on ÉCARTE toute valeur qui en
// porterait un (plutôt que de générer un filtre PostgREST malformé).
// ⚠️ La RLS reste le VRAI cloisonnement — ce filtre est un affichage.
// ⚠️ Ne JAMAIS l'utiliser pour la clôture de journée (SELF strict, FIX B).
export function buildRunsProviderFilter(supabaseQuery, currentUser, visibleEmails) {
  const emails = (Array.isArray(visibleEmails) ? visibleEmails : []).filter(Boolean)
  // ⚠️ L'ADMIN N'EST PAS FILTRÉ CÔTÉ CLIENT (décision Dimitri 2026-08-12).
  // Ce filtre n'a JAMAIS été une barrière de sécurité : la vraie borne est la
  // RLS (`admin_all` pour lui, `orchestrateur_select_pool` pour l'autre). Tel
  // qu'il était, il rendait `[son propre email]` pour l'admin — l'écran de
  // pilotage lui aurait montré les ménages assignés à `admin@fbz.local`,
  // c'est-à-dire RIEN. Ne rien poser lui rend tous les agents, tous les
  // logements, ET les ménages non assignés — qui n'apparaissaient nulle part.
  if (currentUser?.role === 'admin') return supabaseQuery
  if (currentUser?.role !== 'orchestrateur' || emails.length === 0) {
    return supabaseQuery.in('assigned_provider_email', emails)
  }
  const safe = emails.filter((e) => !/["\\]/.test(String(e)))
  const quoted = safe.map((e) => `"${e}"`).join(',')
  return supabaseQuery.or(
    `assigned_provider_email.in.(${quoted}),assigned_provider_email.is.null`
  )
}

const intertitre = { fontSize: 12, fontWeight: 700, color: NEST.vertFonce, letterSpacing: '.06em',
  textTransform: 'uppercase', margin: '10px 0 0' }

// La bascule de la maquette : noire pour ce qui est choisi, avec son icône.
function BasculePlanning({ vue, onChange }) {
  const entrees = [
    { id: 'jour', label: "Aujourd'hui", Icone: CalendarDays },
    { id: 'mois', label: 'Le mois', Icone: CalendarRange },
  ]
  return (
    <div data-testid="bascule-agent-planning" role="tablist"
      style={{ display: 'inline-flex', gap: 3, background: NEST.fondCarte, borderRadius: 11, padding: 3 }}>
      {entrees.map(({ id, label, Icone }) => {
        const actif = id === vue
        return (
          <button key={id} type="button" role="tab" aria-selected={actif} data-testid={`bascule-agent-planning-${id}`}
            onClick={() => onChange(id)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 36, padding: '0 13px', borderRadius: 9,
              border: 'none', background: actif ? NEST.encre : 'transparent', color: actif ? '#fff' : NEST.grisFonce,
              fontFamily: POLICE, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            <Icone size={15} strokeWidth={2} aria-hidden="true" /> {label}
          </button>
        )
      })}
    </div>
  )
}

export default function MenageTodayView({ cfg, currentUser, onControler = null, estControleur = false }) {
  const [vuePlanning, setVuePlanning] = useState('jour') // jamais mémorisé
  const [charge, setCharge] = useState(false)
  const [erreur, setErreur] = useState(false)
  const [aujourdhui, setAujourdhui] = useState(() => todayParisIso())
  const [prenomLu, setPrenomLu] = useState(null)
  const [runs, setRuns] = useState([])
  const [changements, setChangements] = useState([])
  const [logements, setLogements] = useState([])
  const [incidentModal, setIncidentModal] = useState(null) // { run, log }
  const [checklistModal, setChecklistModal] = useState(null) // { run }
  // PR #151 AMENDEMENT P3 — nowHHMM figé au mount (cohérent avec `aujourdhui`).
  const nowHHMM = useMemo(() => nowParisHHMM(), [])

  // ── LA JOURNÉE : une seule porte. Rend la réponse, pour la fin de journée. ──
  const reload = useCallback(async () => {
    try {
      const j = await fetchJourneeAgent()
      setAujourdhui(j.aujourdhui || todayParisIso())
      setPrenomLu(j.prenom)
      setRuns(ordonnerJournee(j.runs))
      setChangements(j.changements.map((e) => ({ ...e, scheduled_date: e.date })))
      setLogements(j.logements)
      setErreur(false)
      return j
    } catch (e) {
      console.error('[mon-planning] lecture', e?.message || 'erreur')
      setErreur(true)
      return null
    } finally {
      setCharge(true)
    }
  }, [])
  useEffect(() => { reload() }, [reload, currentUser?.email])

  const noms = useMemo(() => Object.fromEntries(logements.map((l) => [l.id, l.nom])), [logements])

  // Les modales attendent une « configuration » : on leur en donne seulement ce
  // que le serveur a le droit de dire à cet agent (ses logements, le catalogue
  // sans prix), jamais la configuration réelle.
  const cfgAgent = useMemo(() => ({
    consommables: cfg?.consommables || [],
    props: [{ id: 'agent', logs: logements.map((l) => ({ id: l.id, nom: l.nom, nom_complet: l.nom })) }],
  }), [cfg?.consommables, logements])

  // ── LES CONTRÔLES CONFIÉS ─────────────────────────────────────────────────
  const [controlesJour, setControlesJour] = useState([])
  const [controlesEnErreur, setControlesEnErreur] = useState(false)
  const [infosControles, setInfosControles] = useState({})
  useEffect(() => {
    if (!estControleur) return undefined
    let annule = false
    fetchAControler().then((rep) => {
      if (annule) return
      // JAMAIS son propre ménage dans SES contrôles.
      const liste = (rep?.menages || []).filter((m) => !m.est_mon_menage)
      setControlesJour(liste)
      setControlesEnErreur(false)
      Promise.all(liste.map(async (m) => {
        try { return [m.run_id, await fetchLogInfo(m.logement_id, m.scheduled_date)] }
        catch (e) { console.error('[mon-planning] contrôle log_info', e?.message || 'erreur'); return null }
      })).then((paires) => {
        if (!annule) setInfosControles(Object.fromEntries(paires.filter(Boolean)))
      })
    }).catch((e) => {
      console.error('[mon-planning] contrôles', e?.message || 'erreur')
      if (!annule) setControlesEnErreur(true)
    })
    return () => { annule = true }
  }, [estControleur])

  // ── LES REPRISES : `ReprisesAgent` les lit et les fait ; il nous dit lesquelles. ──
  const [reprises, setReprises] = useState(null) // { erreur, attente, logement, liste }
  const [repriseOuverte, setRepriseOuverte] = useState(null)

  // ── ADR-302 — LES CONSIGNES : une requête par journée, RLS-scopée. ─────────
  const [consignes, setConsignes] = useState([])
  useEffect(() => {
    const logIds = [...new Set(runs.map(r => r.logement_id).filter(Boolean))]
    if (logIds.length === 0) { setConsignes([]); return undefined }
    let cancelled = false
    fetchMyInstructions(logIds).then(({ instructions }) => {
      if (!cancelled) setConsignes(instructions || [])
    })
    return () => { cancelled = true }
  }, [runs, currentUser?.email])
  const consignesDe = useCallback(
    (logementId) => consignes.filter((c) => !c.logement_id || c.logement_id === logementId), [consignes])

  // Hook upload queue — patch menage_runs.photos quand chaque photo est uploadée
  // (helper mutualisé commitUploadedPhoto, ADR-208).
  const onPhotoUploaded = useCallback(async (item) => {
    const next = await commitUploadedPhoto(supabase, item)
    setRuns(prev => prev.map(r => r.id === item.runId ? { ...r, photos: next } : r))
  }, [])
  const { pending, failed } = useMenagePhotoUploadQueue({ onUploaded: onPhotoUploaded })

  // PR #146 — Auto-clôture : après le dernier ménage du jour, le récap part
  // (helper serveur idempotent). Cron 21h Paris reste en place pour edge cases.
  // Clôture = SELF : seul l'agent raisonne sur ses ménages ; l'admin, qui voit
  // tout le parc ici, ne déclenche rien.
  const handleRunCompleted = useCallback(async () => {
    setChecklistModal(null)
    const j = await reload()
    if (!j || currentUser?.role !== 'menage') return
    if (j.runs.some((r) => r.status === 'scheduled' || r.status === 'in_progress')) return
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) return
      await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          type: 'rapport_fin_journee_menage',
          data: { date: j.aujourdhui, triggerSource: 'auto_last_run' },
        }),
      })
    } catch (e) {
      // Le cron de 21h reprendra.
      console.error('[mon-planning] récap de fin de journée', e?.message || 'erreur')
    }
  }, [currentUser?.role, reload])

  const prenom = prenomLu || firstNameFromEmail(currentUser?.email)

  // ── LES CHANGEMENTS ───────────────────────────────────────────────────────
  // Un ménage réassigné ou annulé DISPARAÎT de la liste : sans la tuile, l'agent
  // qui a planifié sa journée le matin se déplace pour rien le soir.
  const aMontrer = useMemo(() => [
    ...retraitsAMontrer(changements), ...ajoutsAMontrer(changements),
    ...datesAMontrer(changements), ...heuresAMontrer(changements),
  ], [changements])
  const [acquitteEnCours, setAcquitteEnCours] = useState(false)
  const acquitter = useCallback(async () => {
    const ids = aMontrer.map((e) => e.id)
    if (!ids.length) return
    setAcquitteEnCours(true)
    // L'horodatage envoyé est ÉCRASÉ par le trigger (heure serveur) — on
    // envoie une valeur pour déclencher, pas pour dater.
    const { data, error } = await supabase
      .from('menage_evenements_agent')
      .update({ vu_le: new Date().toISOString() })
      .in('id', ids)
      .select('id')
    setAcquitteEnCours(false)
    if (error || !data || data.length === 0) {
      console.error('[mon-planning] acquitter', error?.message || 'aucune ligne')
      return
    }
    setChangements([])
  }, [aMontrer])

  // ── LES INFOS DE CHAQUE LOGEMENT (guichet cloisonné) ──────────────────────
  // Adresse, code, horaires, à apporter : une seule source pour tout l'écran.
  const [logInfos, setLogInfos] = useState({})
  const [interventions, setInterventions] = useState([])
  useEffect(() => {
    const cles = [...new Set([...runs, ...interventions].map(r => `${r.logement_id}:${r.scheduled_date}`))]
    if (!cles.length) { setLogInfos({}); return undefined }
    let annule = false
    ;(async () => {
      const entrees = await Promise.all(cles.map(async (cle) => {
        const sep = cle.lastIndexOf(':')
        try {
          return [cle, await fetchLogInfo(cle.slice(0, sep), cle.slice(sep + 1))]
        } catch (e) {
          console.error('[mon-planning] menage_log_info', e?.message || 'erreur')
          return null
        }
      }))
      if (!annule) setLogInfos(Object.fromEntries(entrees.filter(Boolean)))
    })()
    return () => { annule = true }
  }, [runs, interventions])
  const infoDe = useCallback(
    (r) => logInfos[`${r.logement_id}:${r.scheduled_date}`] || null, [logInfos])

  // ── LES INTERVENTIONS DU JOUR ─────────────────────────────────────────────
  // ⚠️ AUCUN FILTRE CLIENT SUR L'AGENT : la policy `interventions_menage_select`
  // borne déjà à `assigned_provider_email = moi`.
  const [validationEnCours, setValidationEnCours] = useState(null)
  const [msgIntervention, setMsgIntervention] = useState(null)
  const [interventionsLues, setInterventionsLues] = useState(null) // null | 'ok' | 'erreur'
  const rechargerInterventions = useCallback(async () => {
    try {
      const { interventions: list, erreur: panne } = await chargerInterventions(supabase, { du: aujourdhui, au: aujourdhui })
      if (panne) { setInterventionsLues('erreur'); return }
      setInterventions(Array.isArray(list) ? list : [])
      setInterventionsLues('ok')
    } catch (e) {
      console.error('[mon-planning] interventions', e?.message || 'erreur')
      setInterventionsLues('erreur')
    }
  }, [aujourdhui])
  useEffect(() => { rechargerInterventions() }, [rechargerInterventions])

  // L'heure EFFECTIVE d'un ménage — la MÊME que celle affichée sur sa carte.
  const heureDeRun = useCallback((run) => {
    const t = infoDe(run)?.timing || null
    const { debut } = heureEffective({
      heureFixee: run?.heure_fixee,
      dureeMin: dureeDuRunAgent(run, infoDe(run)?.menage?.menage_duree_min),
      plageDebut: t?.checkout_time, plageFin: t?.checkin_time,
    })
    return debut
  }, [infoDe])

  // Le tri se refait au RENDU, quand les horaires sont là, avec l'heure affichée
  // (rejeu d'intégration 2026-08-14 : une seule logique d'heure dans la liste).
  const runsOrdonnes = useMemo(
    () => ordonnerJournee(runs, {
      heureFixeeDe: (r) => {
        const m = heureDeRun(r)
        if (!Number.isFinite(m)) return null
        return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
      },
      debutCreneauDe: (r) => infoDe(r)?.timing?.checkout_time || null,
    }),
    [runs, infoDe, heureDeRun]
  )
  const journee = useMemo(
    () => fusionnerJournee({ runs: runsOrdonnes, interventions, heureDeRun }),
    [runsOrdonnes, interventions, heureDeRun]
  )

  const onValiderIntervention = useCallback(async (it) => {
    setValidationEnCours(it.id)
    const { ok } = await validerIntervention(supabase, it.id)
    setValidationEnCours(null)
    if (!ok) {
      setMsgIntervention("L'enregistrement n'est pas passé : rien n'a été marqué. Vérifiez votre connexion puis touchez à nouveau « Terminer l’intervention ».")
      return
    }
    setMsgIntervention(null)
    await rechargerInterventions()
  }, [rechargerInterventions])

  // ── LA JOURNÉE RANGÉE : PROCHAIN · ENSUITE · FAIT ─────────────────────────
  const listeReprises = useMemo(() => reprises?.liste || [], [reprises])
  const rangee = useMemo(() => rangerJournee([
    ...journee.map((e) => (e.type === 'menage'
      ? { cle: `m-${e.item.id}`, type: 'menage', min: heureDeRun(e.item), fini: e.item.status === 'completed', entree: e }
      : { cle: `i-${e.item.id}`, type: 'intervention', min: heureInterventionMin(e.item), fini: estFaite(e.item), entree: e })),
    ...listeReprises.map((r) => ({ cle: `r-${r.id}`, type: 'reprise', fini: false, reprise: r })),
    ...controlesJour.map((m) => ({ cle: `c-${m.run_id}`, type: 'controle', fini: false, menage: m })),
  ]), [journee, listeReprises, controlesJour, heureDeRun])

  const nomRun = useCallback((r) => infoDe(r)?.nom || r.logement_nom || noms[r.logement_id] || 'un logement', [infoDe, noms])

  // Les tuiles : les ménages seuls (une intervention n'est pas un ménage).
  const termines = runs.filter((r) => r.status === 'completed').length
  const limiteControle = controlesJour.map(heureLimiteControle).find(Boolean)
  const interventionsEnPanne = interventionsLues === 'erreur'
  const menagesLus = charge && !erreur && !interventionsEnPanne
  const tuiles = tuilesBandeau({
    menages: menagesLus ? runs.length : null,
    termines,
    controles: controlesEnErreur ? null : controlesJour.length,
    avantControle: limiteControle ? `avant ${limiteControle}` : null,
    reprises: reprises && !reprises.erreur ? reprises.attente : null,
    logementReprise: reprises?.logement,
    changements: charge && !erreur ? aMontrer.length : null,
  })
  // La pastille de « Mon planning » : ce qui reste à faire (le cadre l'écoute).
  const resteAFaire = menagesLus ? runs.length - termines : null
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fbz:agent-a-faire', { detail: resteAFaire }))
  }, [resteAFaire])
  const lignes = useMemo(() => ({
    men: [...journee].map((e) => {
      const it = e.item
      if (e.type === 'menage') {
        const fini = it.status === 'completed'
        const fin = heureFinParis(it.completed_at)
        const min = heureDeRun(it)
        return { cle: `m-${it.id}`, icone: 'men', fini, min,
          titre: [Number.isFinite(min) ? formaterHeureCourte(min) : null, nomRun(it)].filter(Boolean).join(' · '),
          sousTitre: [rangDuMenage(e.rangMenage), Number(it.duree_min) > 0 ? formaterDuree(Number(it.duree_min)) : null].filter(Boolean).join(' · '),
          etiquette: fini ? (fin ? `terminé à ${fin}` : 'terminé') : 'à faire' }
      }
      const min = heureInterventionMin(it)
      return { cle: `i-${it.id}`, icone: 'itv', fini: estFaite(it), min,
        titre: [Number.isFinite(min) ? formaterHeureCourte(min) : null, `Intervention · ${infoDe(it)?.nom || 'un logement'}`].filter(Boolean).join(' · '),
        sousTitre: 'intervention · sans checklist', etiquette: estFaite(it) ? 'faite' : 'à faire' }
    }).sort((a, b) => (a.min ?? 1e9) - (b.min ?? 1e9)),
    ctl: controlesJour.map((m) => {
      const limite = heureLimiteControle(m)
      return { cle: `c-${m.run_id}`, icone: 'ctl', titre: infosControles[m.run_id]?.nom || m.logement_nom || 'un logement',
        sousTitre: limite ? `à contrôler avant ${limite}` : "à contrôler aujourd'hui", etiquette: 'à contrôler' }
    }),
    rep: listeReprises.map((r) => ({ cle: `r-${r.id}`, icone: 'rep', titre: r.logement, sousTitre: r.echeance, etiquette: 'à reprendre' })),
  }), [journee, controlesJour, listeReprises, infoDe, heureDeRun, nomRun, infosControles])

  // Une ligne d'une tuile mène à sa carte, sans changer de page.
  const ancres = useRef({})
  const allerA = useCallback((cle) => {
    const el = ancres.current[cle]
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const incidentDe = useCallback((run) => setIncidentModal({
    run, effectiveRunStatus: getEffectiveRunStatus(run, aujourdhui, nowHHMM, null),
  }), [aujourdhui, nowHHMM])
  // Sur place sans ménage à lui (intervention, contrôle) : l'incident porte le
  // logement seul, `run_id` nul. `id` ne sert qu'à ranger les photos.
  const incidentSurPlace = useCallback((id, logementId) => {
    const run = { id, logement_id: logementId, scheduled_date: aujourdhui, status: 'in_progress' }
    setIncidentModal({ run, sansMenage: true, effectiveRunStatus: getEffectiveRunStatus(run, aujourdhui, nowHHMM, null) })
  }, [aujourdhui, nowHHMM])

  const carte = (t, habit) => {
    const ancre = (el) => { ancres.current[t.cle] = el }
    if (t.type === 'menage') {
      const run = t.entree.item
      return (
        <CarteMenageAgent key={t.cle} ancre={ancre} habit={habit} run={run} index={t.entree.rangMenage} info={infoDe(run)}
          consignes={consignesDe(run.logement_id)}
          onCommencer={() => setChecklistModal({ run })} onIncident={() => incidentDe(run)} />
      )
    }
    if (t.type === 'intervention') {
      const it = t.entree.item
      return (
        <CarteInterventionAgent key={t.cle} ancre={ancre} habit={habit} intervention={it} info={infoDe(it)}
          enCours={validationEnCours === it.id} onValider={onValiderIntervention}
          onIncident={() => incidentSurPlace(it.id, it.logement_id)} />
      )
    }
    if (t.type === 'controle') {
      return (
        <CarteControleAgent key={t.cle} ancre={ancre} habit={habit} menage={t.menage} cfg={cfgAgent}
          info={infosControles[t.menage.run_id] || null} onControler={(x) => onControler && onControler(x)}
          onIncident={() => incidentSurPlace(t.menage.run_id, t.menage.logement_id)} />
      )
    }
    const r = t.reprise
    return (
      <div key={t.cle} ref={ancre}>
        <CartePlanning testId={`carte-reprise-${r.id}`} habit={habit} teinte="aub" heure={r.echeance}
          titre={`À reprendre · ${r.logement}`} sousTitre={r.echeance}>
          <BoutonCarte testId={`reprise-ouvrir-${r.id}`} onClick={() => setRepriseOuverte(r.id)}>Reprendre ce ménage</BoutonCarte>
          {r.run_id && (
            <BoutonIncident testId={`reprise-incident-${r.id}`}
              onClick={() => incidentDe({ id: r.run_id, logement_id: r.logement_id, scheduled_date: r.scheduled_date, status: 'completed' })} />
          )}
        </CartePlanning>
      </div>
    )
  }

  // « Rien » et « Tout est fait » ne se disent que si TOUT a été lu.
  const toutLu = charge && !erreur && interventionsLues === 'ok' && !controlesEnErreur && !reprises?.erreur
  const rien = toutLu && !rangee.prochain && !rangee.fait.length

  return (
    <div data-testid="mon-planning" style={{ padding: '0 0 80px', fontFamily: POLICE, color: NEST.encre, background: NEST.fondPage }}>
      <BandeauAgent date={dateTitreFR(aujourdhui)} prenom={prenom} tuiles={tuiles} lignes={lignes}
        changements={aMontrer.map((e) => ({ id: e.id, ...ligneChangement(e) }))}
        onAller={allerA} onVu={acquitter} vuEnCours={acquitteEnCours} />

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ marginBottom: 6 }}><BasculePlanning vue={vuePlanning} onChange={setVuePlanning} /></div>

        {vuePlanning === 'mois' && <MonMoisAgent currentUser={currentUser} estControleur={estControleur}
          changements={aMontrer} onChecklist={(run) => setChecklistModal({ run })} onIncident={incidentDe} />}

        {vuePlanning === 'jour' && (<>
          {!charge && <p data-testid="planning-lecture" style={{ fontSize: 12.5, color: NEST.gris, margin: '8px 2px' }}>On lit…</p>}
          {erreur && (
            <div data-testid="planning-erreur" style={{ fontSize: 12.5, color: NEST.aubTexte, background: NEST.aubPale, borderRadius: 10, padding: '10px 12px' }}>
              La lecture de votre planning n'a pas abouti. Vérifiez votre connexion.
              <button type="button" onClick={reload} style={{ marginLeft: 8, minHeight: 36, padding: '0 12px', borderRadius: 9, border: `1px solid ${NEST.aubBord}`,
                background: '#fff', color: NEST.aubTexte, fontFamily: POLICE, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} strokeWidth={2} /> Réessayer
              </button>
            </div>
          )}
          {interventionsEnPanne && (
            <div data-testid="planning-erreur-interventions" style={{ fontSize: 12.5, color: NEST.aubTexte, background: NEST.aubPale, borderRadius: 10, padding: '10px 12px' }}>
              La lecture de vos interventions n'a pas abouti. Vérifiez votre connexion.
              <button type="button" onClick={rechargerInterventions} style={{ marginLeft: 8, minHeight: 36, padding: '0 12px', borderRadius: 9, border: `1px solid ${NEST.aubBord}`,
                background: '#fff', color: NEST.aubTexte, fontFamily: POLICE, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} strokeWidth={2} /> Réessayer
              </button>
            </div>
          )}
          {(pending > 0 || failed > 0) && (
            <div style={{ alignSelf: 'flex-start', fontSize: 11.5, background: NEST.aubPale, border: `1px solid ${NEST.aubBord}`, color: NEST.aubTexte, borderRadius: 16, padding: '4px 11px' }}>
              {pending + failed} photo{pending + failed > 1 ? 's' : ''} en cours d'envoi{failed > 0 ? ` · ${failed} échec${failed > 1 ? 's' : ''} (nouvel essai automatique)` : ''}
            </div>
          )}
          {msgIntervention && (
            <div data-testid="today-erreur-intervention" role="status" style={{ display: 'flex', gap: 9, background: NEST.aubPale, border: `1px solid ${NEST.aubBord}`,
              borderRadius: 10, padding: '9px 11px', fontSize: 12, color: NEST.aubTexte }}>
              <span style={{ flex: 1 }}>{msgIntervention}</span>
              <button type="button" data-testid="today-erreur-intervention-fermer" onClick={() => setMsgIntervention(null)} aria-label="Masquer ce message"
                title="Masquer ce message" style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer' }}><X size={15} strokeWidth={2} /></button>
            </div>
          )}
          {rien && (
            <div data-testid="planning-vide" style={{ background: '#fff', border: `1px solid ${NEST.bordCarte}`, borderRadius: 14, padding: '14px 16px', fontSize: 13, color: NEST.gris }}>
              Aucun ménage programmé aujourd'hui.
            </div>
          )}
          {rangee.prochain && (<>
            <div data-testid="planning-prochain" style={{ ...intertitre, marginTop: 0 }}>{titreProchain(rangee.prochain)}</div>
            {carte(rangee.prochain, 'grand')}
          </>)}
          {toutLu && !rangee.prochain && rangee.fait.length > 0 && (
            <div data-testid="planning-tout-fait" style={{ display: 'flex', alignItems: 'center', gap: 9, background: NEST.vertLigne, borderRadius: 14, padding: '14px 16px', color: NEST.vertFonce }}>
              <CircleCheck size={18} strokeWidth={2} aria-hidden="true" /><b style={{ fontSize: 14 }}>Tout est fait pour aujourd’hui.</b>
            </div>
          )}
          {rangee.ensuite.length > 0 && (<>
            <div data-testid="planning-ensuite" style={intertitre}>Ensuite · {rangee.ensuite.length}</div>
            {rangee.ensuite.map((t) => carte(t, 'plie'))}
          </>)}
          {rangee.fait.length > 0 && (<>
            <div data-testid="planning-fait" style={intertitre}>Fait · {rangee.fait.length}</div>
            {rangee.fait.map((t) => carte(t, 'fini'))}
          </>)}
        </>)}
      </div>

      {/* Il reste monté : il lit les reprises et compte ce qui attend. */}
      <ReprisesAgent currentUser={currentUser} noms={noms} onCharge={setReprises}
        ouverte={repriseOuverte} onFermer={() => setRepriseOuverte(null)} />

      {incidentModal && (
        <IncidentModal
          incident={null}
          zIndexBase={incidentModal.surChecklist ? 1200 : undefined}
          cfg={cfgAgent}
          data={null}
          isAdmin={false}
          userLogIds={[incidentModal.run.logement_id]}
          defaultLogId={incidentModal.run.logement_id}
          // PR #153 (Q1) : verrou heure unifié via effectiveRunStatus
          effectiveRunStatus={incidentModal.effectiveRunStatus || null}
          run={incidentModal.run}
          onClose={() => setIncidentModal(null)}
          onSave={async (payload) => {
            // L'erreur est loguée dans createIncident ; elle remonte au modal,
            // qui affiche le bandeau et rend la main.
            await createIncident({
              logement_id: payload.logId,
              run_id: incidentModal.sansMenage ? null : incidentModal.run?.id || null,
              source: 'run',
              reported_by: currentUser.email,
              voyageur: payload.voyageur || null,
              casse_interne: !!payload.casse_interne,
              incident_date: payload.date || null,
              degradations: payload.degradations || [],
              photos: payload.photos || [],
              label: (payload.degradations?.[0]?.type) || 'Incident multi-dégradations',
              description: (payload.degradations || []).map(d => `${d.type} (${d.nombre || 1}) ${d.precision || ''}`.trim()).join(' · '),
              status: STATUS.EN_COURS,
            })
            setIncidentModal(null)
            reload()
          }}
        />
      )}

      {checklistModal && (
        <ChecklistRunModal
          run={checklistModal.run}
          currentUser={currentUser}
          cfg={cfgAgent}
          onClose={() => setChecklistModal(null)}
          onCompleted={handleRunCompleted}
          onIncident={(run) => setIncidentModal({
            run,
            effectiveRunStatus: getEffectiveRunStatus(run, aujourdhui, nowHHMM, null),
            surChecklist: true, // la modale passe AU-DESSUS de la checklist
          })}
        />
      )}
    </div>
  )
}
